import * as React from 'react';
import { UserCog } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { MultiSelect } from '@/components/forms/MultiSelect';
import {
  getCoordinatorsHelperText,
  getCoordinatorsLockedHelperText,
} from '@/components/establishments/establishment-form-utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import type {
  EstablishmentFormFieldValidation,
  EstablishmentFormState,
} from '@/types/establishment.types';

/** {@link Autocomplete} option for manager and coordinator pickers. */
interface SelectOption {
  value: string;
  label: string;
}

/** Props for {@link EstablishmentManagersSection}. */
interface Props {
  form: EstablishmentFormState;
  onFieldChange: (field: string, value: unknown) => void;
  onBlur: (field: string) => void;
  validation: EstablishmentFormFieldValidation;
  isManager: boolean;
  managerLabel: string;
  managerOptions: SelectOption[];
  managerLoading: boolean;
  onManagerSearch: (query: string) => void;
  coordinatorOptions: SelectOption[];
  coordinatorLoading: boolean;
  onCoordinatorSearch: (query: string) => void;
}

/** Coordinator / manager assignment fields. */
export function EstablishmentManagersSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  isManager,
  managerLabel,
  managerOptions,
  managerLoading,
  onManagerSearch,
  coordinatorOptions,
  coordinatorLoading,
  onCoordinatorSearch,
}: Readonly<Props>) {
  function handleManagedByChange(value: string): void {
    onFieldChange('managedById', value);
  }
  function handleManagedByBlur(): void {
    onBlur('managedById');
  }
  function handleCoordinatorChange(values: string[]): void {
    onFieldChange('coordinatorIds', values);
  }
  function handleCoordinatorBlur(): void {
    onBlur('coordinatorIds');
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <UserCog className="w-4 h-4" />
        Coordinateurs gestionnaires
      </h4>
      <div className="grid grid-cols-1 gap-4">
        {renderManagedByField({
          isManager,
          managerLabel,
          form,
          managerOptions,
          managerLoading,
          onManagerSearch,
          onManagedByChange: handleManagedByChange,
          onManagedByBlur: handleManagedByBlur,
          validation,
        })}
        <MultiSelect
          label={getRoleLabelPlural('coordinator')}
          placeholder="Sélectionner les coordinateurs"
          options={coordinatorOptions}
          value={form.coordinatorIds}
          onChange={handleCoordinatorChange}
          onBlur={handleCoordinatorBlur}
          onSearch={onCoordinatorSearch}
          isSearchLoading={coordinatorLoading}
          minSearchLength={2}
          maxVisibleTags={3}
          showSelectAll={false}
          disabled={!form.managedById}
          helperText={
            form.managedById ? getCoordinatorsHelperText() : getCoordinatorsLockedHelperText()
          }
          inputSize="sm"
        />
      </div>
    </div>
  );
}

/** Manager autocomplete, or a locked label when the actor is already a manager. */
function renderManagedByField(params: {
  isManager: boolean;
  managerLabel: string;
  form: EstablishmentFormState;
  managerOptions: SelectOption[];
  managerLoading: boolean;
  onManagerSearch: (query: string) => void;
  onManagedByChange: (value: string) => void;
  onManagedByBlur: () => void;
  validation: EstablishmentFormFieldValidation;
}): React.ReactNode {
  if (params.isManager) {
    return (
      <Input
        label="Géré par"
        value={params.managerLabel}
        disabled
        helperText="Vous êtes le manager responsable de cet établissement"
        inputSize="sm"
      />
    );
  }
  return (
    <Autocomplete
      label="Géré par"
      value={params.form.managedById}
      onChange={params.onManagedByChange}
      onBlur={params.onManagedByBlur}
      options={params.managerOptions}
      onSearch={params.onManagerSearch}
      isLoading={params.managerLoading}
      minSearchLength={2}
      placeholder="Sélectionner un manager (optionnel)"
      helperText="Manager responsable de cet établissement (optionnel)"
      state={params.validation.getFieldState('managedById')}
      error={params.validation.getFieldError('managedById')}
      inputSize="sm"
    />
  );
}
